import { useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import SplashCursor, { type SplashCursorHandle } from "./SplashCursor";
import Header from "./Header";

const SHOW_HEADER = false;

export default function Hero() {
  const splashRef = useRef<SplashCursorHandle>(null);
  // throttle so we don't fire a burst on every single mousemove pixel
  const lastBurstRef = useRef<number>(0);

  const handleBurst = useCallback((target: HTMLElement | null) => {
    if (!target) return;
    const now = performance.now();
    // fire at most ~6 bursts per second per element area
    if (now - lastBurstRef.current < 160) return;
    lastBurstRef.current = now;
    splashRef.current?.burst(target.getBoundingClientRect());
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-background-50 flex flex-col">
      {/* GPU fluid simulation background — boosted colors for cream canvas */}
      <div className="absolute inset-0 z-0">
        <SplashCursor
          ref={splashRef}
          DENSITY_DISSIPATION={2.2}
          VELOCITY_DISSIPATION={1.6}
          SPLAT_RADIUS={0.28}
          SPLAT_FORCE={7200}
          CURL={4}
          COLOR_UPDATE_SPEED={8}
          BACK_COLOR={{ r: 0, g: 0, b: 0 }}
        />
      </div>

      {/* very light cream tint so text reads cleanly over swirling fluid */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-background-50/55" />

      {SHOW_HEADER ? <Header /> : null}

      {/* hero content — headline + brand intro, vertically centered */}
      <div
        className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 md:px-10 text-center"
        onMouseEnter={(e) => handleBurst(e.currentTarget)}
        onMouseMove={(e) => handleBurst(e.currentTarget)}
      >
        <h1 className="font-heading font-black tracking-[0.02em] text-foreground-950 leading-[0.95] whitespace-nowrap text-[11vw] xl:text-[9rem] flex items-baseline justify-center gap-[0.4em]">
          <span>複刻</span>
          <span>文化</span>
        </h1>

        <p className="mt-5 font-heading italic text-foreground-800 text-xl md:text-2xl tracking-[0.14em]">
          FUKO
        </p>

        <p className="mt-2 font-body text-foreground-600 text-base md:text-lg tracking-[0.06em]">
          讓經典重新被閱讀
        </p>

        <div className="mt-10 h-px w-16 bg-foreground-300" />

        <h2 className="mt-8 font-heading font-black tracking-[-0.02em] text-foreground-950 text-3xl md:text-4xl leading-[0.95]">
          認識複刻文化
        </h2>

        <p className="mt-4 font-body text-[14px] md:text-[15px] text-foreground-700 leading-[1.8] max-w-[560px]">
          複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過重新整理、編輯與出版，讓不同時代的作品重新進入當代閱讀。
        </p>

        <div className="mt-7">
          <Link
            to="/about"
            className="inline-flex items-center justify-center px-8 py-2.5 rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 text-[13px] font-label italic transition-colors cursor-pointer whitespace-nowrap"
          >
            認識我們
          </Link>
        </div>
      </div>
    </section>
  );
}